<template>
  <div class="an-root">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="an-topbar">
      <div class="brand">
        <button class="back-btn" @click="goBack" title="Back to dashboard"><v-icon size="20" color="#14663a">mdi-arrow-left</v-icon></button>
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">Executive Comparisons &amp; Analytics</div>
          <div class="brand-sub">Wildlife Trust of India · Strategic Executive Suite</div>
        </div>
      </div>
      <div class="topbar-actions">
        <span class="fy-chip" v-if="financialYear">FY {{ financialYear }}</span>
        <button class="btn ghost" :disabled="loading" @click="fetchAllData"><v-icon size="17" :class="{ spin: loading }">mdi-refresh</v-icon><span>Refresh</span></button>
        <button class="btn solid" @click="logout"><v-icon size="17">mdi-logout</v-icon><span>Logout</span></button>
      </div>
    </header>

    <!-- ── Main Navigation Tabs ────────────────────────────────────────── -->
    <nav class="an-nav">
      <div class="nav-tabs">
        <button class="nav-tab" :class="{ active: activeTab === 'scorecards' }" @click="switchTab('scorecards')">
          <v-icon size="18" :color="activeTab === 'scorecards' ? '#14663a' : '#6b7a72'">mdi-folder-account-outline</v-icon>
          <span>Division &amp; Team Scorecards</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'talent_matrix' }" @click="switchTab('talent_matrix')">
          <v-icon size="18" :color="activeTab === 'talent_matrix' ? '#14663a' : '#6b7a72'">mdi-grid</v-icon>
          <span>Talent 9-Box Matrix</span>
          <span class="badge" v-if="talentData && talentData.totalEmployees">{{ talentData.totalEmployees }}</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'projects' }" @click="switchTab('projects')">
          <v-icon size="18" :color="activeTab === 'projects' ? '#14663a' : '#6b7a72'">mdi-compass-outline</v-icon>
          <span>Project Benchmarking</span>
          <span class="badge" v-if="projectList.length">{{ projectList.length }}</span>
        </button>
        <button class="nav-tab" :class="{ active: activeTab === 'donations_yoy' }" @click="switchTab('donations_yoy')">
          <v-icon size="18" :color="activeTab === 'donations_yoy' ? '#14663a' : '#6b7a72'">mdi-chart-timeline-variant</v-icon>
          <span>Multi-Year YoY Donations</span>
        </button>
      </div>
    </nav>

    <div v-if="loading" class="loader-wrap"><v-progress-circular indeterminate color="#14663a" size="52" width="4" /></div>

    <main v-else class="an-body">
      <div v-if="error" class="banner error"><v-icon size="18" color="#c62828">mdi-alert-circle-outline</v-icon> {{ error }}</div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 1: DIVISION & TEAM SCORECARDS                                   -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-if="activeTab === 'scorecards'">
        <!-- Adoption Funnel -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-progress-check</v-icon> Target Process Adoption</div>
        <div v-if="a.adoption.submissionRate < 25" class="insight">
          <v-icon size="18" color="#b56b00">mdi-alert</v-icon>
          <span>Only <b>{{ a.adoption.submissionRate }}%</b> of targets have been submitted and <b>{{ a.adoption.notStarted }}</b> of {{ a.adoption.employees }} employees haven't submitted any — target scores below are provisional until the KPI process is actively used.</span>
        </div>
        <section class="funnel">
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.assigned) }}</div><div class="fn-l">Targets assigned</div></div>
          <v-icon size="20" color="#c9d6ce">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.submitted) }}</div><div class="fn-l">Submitted <em>{{ a.adoption.submissionRate }}%</em></div></div>
          <v-icon size="20" color="#c9d6ce">mdi-chevron-right</v-icon>
          <div class="fn-step"><div class="fn-v">{{ num(a.adoption.approved) }}</div><div class="fn-l">Approved <em>{{ a.adoption.approvalRate }}%</em></div></div>
          <div class="fn-step warn"><div class="fn-v">{{ num(a.adoption.notStarted) }}</div><div class="fn-l">Not started (0 submitted)</div></div>
        </section>

        <!-- Division Scorecards -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-folder-multiple</v-icon> Division Scorecards</div>
        <section class="card table-card">
          <table class="an-table">
            <thead><tr><th>Division</th><th class="r">Staff</th><th class="r">Targets</th><th>Effectiveness</th><th class="r">Attendance</th><th class="r">Submission</th><th class="r">Not Met</th><th class="r">Exits</th></tr></thead>
            <tbody>
              <tr v-for="d in a.divisions" :key="d.division">
                <td class="strong">{{ d.division }}</td>
                <td class="r">{{ d.staff }}</td>
                <td class="r">{{ d.targets }}</td>
                <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: d.score + '%', background: scoreColor(d.score) }"></div></div><span class="score-num">{{ d.score }}</span></div></td>
                <td class="r">{{ d.avgAttendance }}%</td>
                <td class="r">{{ d.submissionRate }}%</td>
                <td class="r"><span :class="d.notMet > 0 ? 'bad' : 'dim'">{{ d.notMet }}</span></td>
                <td class="r"><span :class="d.exits > 0 ? 'bad' : 'dim'">{{ d.exits }}</span></td>
              </tr>
            </tbody>
          </table>
        </section>

        <!-- Manager Effectiveness -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-account-tie</v-icon> Manager Effectiveness &amp; Approval Bottlenecks</div>
        <section class="card table-card">
          <table class="an-table">
            <thead><tr><th>Manager</th><th class="r">Team</th><th>Team effectiveness</th><th class="r">Pending approvals</th><th class="r">Oldest pending</th></tr></thead>
            <tbody>
              <tr v-for="m in a.managers" :key="m.id">
                <td class="strong">{{ m.name || '—' }}</td>
                <td class="r">{{ m.teamSize }}</td>
                <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: m.teamAvgScore + '%', background: scoreColor(m.teamAvgScore) }"></div></div><span class="score-num">{{ m.teamAvgScore }}</span></div></td>
                <td class="r"><span :class="m.pendingApprovals > 0 ? 'warn-txt' : 'dim'">{{ m.pendingApprovals }}</span></td>
                <td class="r"><span :class="m.oldestPendingDays > 0 ? 'warn-txt' : 'dim'">{{ m.oldestPendingDays ? m.oldestPendingDays + 'd' : '—' }}</span></td>
              </tr>
              <tr v-if="!a.managers.length"><td colspan="5" class="dim center">No reporting-manager data.</td></tr>
            </tbody>
          </table>
        </section>

        <!-- Employee Leaderboard -->
        <div class="section-eyebrow"><v-icon size="16" color="#14663a">mdi-trophy-outline</v-icon> Employee Effectiveness Leaderboard</div>
        <section class="controls">
          <div class="view-toggle">
            <button :class="['vt', view === 'top' && 'on']" @click="view = 'top'">Top</button>
            <button :class="['vt', view === 'attention' && 'on']" @click="view = 'attention'">Needs attention</button>
            <button :class="['vt', view === 'all' && 'on']" @click="view = 'all'">All</button>
          </div>
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="search" type="text" placeholder="Search name or ID" /></div>
            <select v-model="divisionFilter" class="sel"><option value="">All divisions</option><option v-for="d in divisionNames" :key="d" :value="d">{{ d }}</option></select>
          </div>
        </section>
        <section class="card table-card">
          <table class="an-table">
            <thead><tr><th>#</th><th>Name</th><th>Division</th><th>Effectiveness</th><th class="r">Achieve</th><th class="r">On-time</th><th class="r">Attend</th><th class="r">Submit</th><th class="r">PMR</th></tr></thead>
            <tbody>
              <tr v-for="(e, i) in shownEmployees" :key="e.id">
                <td class="dim">{{ i + 1 }}</td>
                <td class="strong">{{ e.name }} <span class="dim">{{ e.employeeId ? '· ' + e.employeeId : '' }}</span><span v-if="e.onProbation" class="prob-tag">probation</span></td>
                <td><span class="proj-chip">{{ e.division }}</span></td>
                <td><div class="score-cell"><div class="score-bar"><div class="score-fill" :style="{ width: e.score + '%', background: scoreColor(e.score) }"></div></div><span class="score-num">{{ e.score }}</span></div></td>
                <td class="r">{{ e.metPercent }}%</td>
                <td class="r">{{ e.onTimeRate == null ? '—' : e.onTimeRate + '%' }}</td>
                <td class="r">{{ e.attendancePct }}%</td>
                <td class="r">{{ e.submissionRate }}%</td>
                <td class="r">{{ e.pmrRating == null ? '—' : e.pmrRating }}</td>
              </tr>
              <tr v-if="!shownEmployees.length"><td colspan="9" class="dim center">No employees match.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 2: 9-BOX TALENT & PERFORMANCE MATRIX                            -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'talent_matrix'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-grid</v-icon> Talent &amp; Performance 9-Box Matrix
          <span class="eye-sub">PMR Appraisal Rating (Potential) vs. KPI Target Achievement (Performance)</span>
        </div>

        <div class="matrix-overview-grid">
          <div class="mo-card star">
            <div class="mo-val">{{ getQuadrantCount(['high_high', 'high_med']) }}</div>
            <div class="mo-title">High Potentials &amp; Leaders</div>
            <div class="mo-sub">Top delivery &amp; high appraisal rating</div>
          </div>
          <div class="mo-card core">
            <div class="mo-val">{{ getQuadrantCount(['med_med', 'high_low', 'med_low']) }}</div>
            <div class="mo-title">Core Contributors</div>
            <div class="mo-sub">Dependable solid delivery</div>
          </div>
          <div class="mo-card risk">
            <div class="mo-val">{{ getQuadrantCount(['low_low', 'low_med']) }}</div>
            <div class="mo-title">At-Risk / Coaching Needed</div>
            <div class="mo-sub">Low completion &amp; development scope</div>
          </div>
        </div>

        <!-- Matrix Filters -->
        <div class="controls" style="margin-top:16px;">
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="talentSearch" type="text" placeholder="Search staff name..." /></div>
            <select v-model="talentDivFilter" class="sel">
              <option value="">All divisions</option>
              <option v-for="d in divisionNames" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
          <div class="matrix-legend">
            <span class="leg-item"><span class="leg-dot green"></span> High</span>
            <span class="leg-item"><span class="leg-dot blue"></span> Core</span>
            <span class="leg-item"><span class="leg-dot red"></span> Action Required</span>
          </div>
        </div>

        <!-- 3x3 Matrix Grid -->
        <div class="matrix-container card">
          <div class="matrix-y-axis-label">▲ Potential (Appraisal / PMR Rating)</div>
          <div class="matrix-wrapper">
            <div class="matrix-grid">
              <!-- Row 1: High Potential -->
              <div class="matrix-cell row-high col-low" :class="{ selected: selectedQuadrantKey === 'low_high' }" @click="selectQuadrant('low_high')">
                <div class="mc-head"><span class="mc-tag orange">Enigmas</span><span class="mc-count">{{ filteredQuadrantEmps('low_high').length }}</span></div>
                <div class="mc-desc">High Potential · Low Target Met</div>
              </div>
              <div class="matrix-cell row-high col-med" :class="{ selected: selectedQuadrantKey === 'med_high' }" @click="selectQuadrant('med_high')">
                <div class="mc-head"><span class="mc-tag blue">High Potential</span><span class="mc-count">{{ filteredQuadrantEmps('med_high').length }}</span></div>
                <div class="mc-desc">High Potential · Med Target Met</div>
              </div>
              <div class="matrix-cell row-high col-high" :class="{ selected: selectedQuadrantKey === 'high_high' }" @click="selectQuadrant('high_high')">
                <div class="mc-head"><span class="mc-tag green">Star Talent</span><span class="mc-count">{{ filteredQuadrantEmps('high_high').length }}</span></div>
                <div class="mc-desc">High Potential · High Target Met</div>
              </div>

              <!-- Row 2: Medium Potential -->
              <div class="matrix-cell row-med col-low" :class="{ selected: selectedQuadrantKey === 'low_med' }" @click="selectQuadrant('low_med')">
                <div class="mc-head"><span class="mc-tag orange">Needs Coaching</span><span class="mc-count">{{ filteredQuadrantEmps('low_med').length }}</span></div>
                <div class="mc-desc">Med Potential · Low Target Met</div>
              </div>
              <div class="matrix-cell row-med col-med" :class="{ selected: selectedQuadrantKey === 'med_med' }" @click="selectQuadrant('med_med')">
                <div class="mc-head"><span class="mc-tag blue">Core Professionals</span><span class="mc-count">{{ filteredQuadrantEmps('med_med').length }}</span></div>
                <div class="mc-desc">Med Potential · Med Target Met</div>
              </div>
              <div class="matrix-cell row-med col-high" :class="{ selected: selectedQuadrantKey === 'high_med' }" @click="selectQuadrant('high_med')">
                <div class="mc-head"><span class="mc-tag green">High Performers</span><span class="mc-count">{{ filteredQuadrantEmps('high_med').length }}</span></div>
                <div class="mc-desc">Med Potential · High Target Met</div>
              </div>

              <!-- Row 3: Low Potential -->
              <div class="matrix-cell row-low col-low" :class="{ selected: selectedQuadrantKey === 'low_low' }" @click="selectQuadrant('low_low')">
                <div class="mc-head"><span class="mc-tag red">At-Risk</span><span class="mc-count">{{ filteredQuadrantEmps('low_low').length }}</span></div>
                <div class="mc-desc">Low Potential · Low Target Met</div>
              </div>
              <div class="matrix-cell row-low col-med" :class="{ selected: selectedQuadrantKey === 'med_low' }" @click="selectQuadrant('med_low')">
                <div class="mc-head"><span class="mc-tag orange">Steady Workers</span><span class="mc-count">{{ filteredQuadrantEmps('med_low').length }}</span></div>
                <div class="mc-desc">Low Potential · Med Target Met</div>
              </div>
              <div class="matrix-cell row-low col-high" :class="{ selected: selectedQuadrantKey === 'high_low' }" @click="selectQuadrant('high_low')">
                <div class="mc-head"><span class="mc-tag blue">Execution Specialists</span><span class="mc-count">{{ filteredQuadrantEmps('high_low').length }}</span></div>
                <div class="mc-desc">Low Potential · High Target Met</div>
              </div>
            </div>
            <div class="matrix-x-axis-label">Performance (KPI Target Met %) ►</div>
          </div>
        </div>

        <!-- Selected Quadrant Staff Drilldown -->
        <div v-if="selectedQuadrant" class="quadrant-detail-box card" style="margin-top:16px;">
          <div class="qd-head">
            <div>
              <div class="qd-title"><v-icon size="20" :color="selectedQuadrant.color">mdi-account-group</v-icon> {{ selectedQuadrant.title }}</div>
              <div class="qd-sub">{{ selectedQuadrant.desc }} · {{ selectedQuadrantEmps.length }} Staff</div>
            </div>
            <button class="btn ghost small" @click="selectedQuadrantKey = ''"><v-icon size="16">mdi-close</v-icon> Clear Selection</button>
          </div>
          <table class="an-table" v-if="selectedQuadrantEmps.length">
            <thead><tr><th>#</th><th>Name</th><th>Division</th><th>Manager</th><th class="r">KPI Met %</th><th class="r">PMR</th><th class="r">Effectiveness</th></tr></thead>
            <tbody>
              <tr v-for="(e, i) in selectedQuadrantEmps" :key="e.id">
                <td class="dim">{{ i + 1 }}</td>
                <td class="strong">{{ e.name }} <span class="dim">{{ e.employeeId ? '· ' + e.employeeId : '' }}</span></td>
                <td><span class="proj-chip">{{ e.division }}</span></td>
                <td>{{ e.manager || '—' }}</td>
                <td class="r strong">{{ e.metPercent }}%</td>
                <td class="r">{{ e.pmrRating != null ? e.pmrRating : '—' }}</td>
                <td class="r"><span class="score-num" :style="{ color: scoreColor(e.score) }">{{ e.score }}</span></td>
              </tr>
            </tbody>
          </table>
          <div v-else class="dim center" style="padding:20px;">No staff in this quadrant match your search/filters.</div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 3: PROJECT PERFORMANCE BENCHMARK                                -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'projects'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-compass-outline</v-icon> Project Delivery &amp; Resource Benchmark
          <span class="eye-sub">Cross-project delivery, team allocation, and target milestones</span>
        </div>

        <div class="proj-kpi-grid">
          <div class="pk-card">
            <div class="pk-val">{{ projectList.length }}</div>
            <div class="pk-lbl">Active Conservation Projects</div>
          </div>
          <div class="pk-card top">
            <div class="pk-val">{{ projectList.filter(p => p.status === 'top').length }}</div>
            <div class="pk-lbl">Top-Tier Delivery (Score ≥ 70)</div>
          </div>
          <div class="pk-card warn">
            <div class="pk-val">{{ projectList.filter(p => p.status === 'lagging').length }}</div>
            <div class="pk-lbl">Needs Support (Score &lt; 40)</div>
          </div>
          <div class="pk-card">
            <div class="pk-val">{{ avgProjectTeamSize }}</div>
            <div class="pk-lbl">Average Staff per Project</div>
          </div>
        </div>

        <!-- Project Controls -->
        <section class="controls" style="margin-top:16px;">
          <div class="view-toggle">
            <button :class="['vt', projectFilterStatus === 'all' && 'on']" @click="projectFilterStatus = 'all'">All Projects</button>
            <button :class="['vt', projectFilterStatus === 'top' && 'on']" @click="projectFilterStatus = 'top'">Top Performing</button>
            <button :class="['vt', projectFilterStatus === 'lagging' && 'on']" @click="projectFilterStatus = 'lagging'">Lagging</button>
          </div>
          <div class="filters">
            <div class="search-box"><v-icon size="17" color="#8a978f">mdi-magnify</v-icon><input v-model="projectSearch" type="text" placeholder="Search project name..." /></div>
            <select v-model="projectDivFilter" class="sel">
              <option value="">All divisions</option>
              <option v-for="d in projectDivisions" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>
        </section>

        <!-- Project Table -->
        <section class="card table-card">
          <table class="an-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Project Name</th>
                <th>Division</th>
                <th>Project Lead</th>
                <th class="r">Team Size</th>
                <th class="r">Targets</th>
                <th>Completion Rate</th>
                <th class="r">On-Time</th>
                <th class="r">Project Score</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in filteredProjects" :key="p.id">
                <td class="dim">{{ idx + 1 }}</td>
                <td class="strong">
                  {{ p.projectName }}
                  <span v-if="p.isFundRaise" class="fr-tag" title="Fundraising Enabled">Fundraise</span>
                </td>
                <td><span class="proj-chip">{{ p.division }}</span></td>
                <td>{{ p.projectHead }}</td>
                <td class="r strong">{{ p.teamSize }}</td>
                <td class="r">{{ p.totalTargets }}</td>
                <td>
                  <div class="score-cell">
                    <div class="score-bar"><div class="score-fill" :style="{ width: p.completionRate + '%', background: scoreColor(p.completionRate) }"></div></div>
                    <span class="score-num">{{ p.completionRate }}%</span>
                  </div>
                </td>
                <td class="r">{{ p.onTimeRate }}%</td>
                <td class="r"><span class="score-num" :style="{ color: scoreColor(p.projectScore) }">{{ p.projectScore }}</span></td>
                <td>
                  <span class="status-pill" :class="p.status">
                    {{ p.status === 'top' ? 'Top Tier' : (p.status === 'lagging' ? 'Needs Support' : 'On Track') }}
                  </span>
                </td>
              </tr>
              <tr v-if="!filteredProjects.length"><td colspan="10" class="dim center">No projects match the criteria.</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <!-- TAB 4: MULTI-YEAR YOY DONATION COMPARISON                           -->
      <!-- ═══════════════════════════════════════════════════════════════════ -->
      <div v-else-if="activeTab === 'donations_yoy'">
        <div class="section-eyebrow">
          <v-icon size="16" color="#14663a">mdi-chart-timeline-variant</v-icon> Multi-Year Financial &amp; Donation YoY Comparison
          <span class="eye-sub">3-Year Side-by-Side Trajectory, Inflows &amp; Seasonality Curves</span>
        </div>

        <div class="yoy-summary-grid" v-if="yoyData">
          <div class="yoy-card" v-for="fy in yoyData.comparison" :key="fy.financialYear">
            <div class="yoy-head">
              <span class="yoy-fy">FY {{ fy.financialYear }}</span>
              <span class="yoy-tx">{{ num(fy.totalSuccess) }} Donations</span>
            </div>
            <div class="yoy-val">{{ formatMoney(fy.totalInr) }}</div>
            <div class="yoy-sub">Foreign: <b>{{ formatUsd(fy.totalUsd) }}</b></div>
            <div class="yoy-foot">Avg. Ticket: <b>{{ formatMoney(fy.avgInrTicket) }}</b></div>
          </div>
          <div class="yoy-growth-card" :class="(yoyData.yoyGrowthInr || 0) >= 0 ? 'pos' : 'neg'">
            <div class="yg-lbl">YoY Growth (Current vs Last FY)</div>
            <div class="yg-val">
              <v-icon :color="(yoyData.yoyGrowthInr || 0) >= 0 ? '#2e7d32' : '#c62828'">
                {{ (yoyData.yoyGrowthInr || 0) >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
              </v-icon>
              {{ yoyData.yoyGrowthInr != null ? (yoyData.yoyGrowthInr > 0 ? '+' : '') + yoyData.yoyGrowthInr + '%' : '—' }}
            </div>
            <div class="yg-sub">Previous FY YoY: {{ yoyData.prevYoyGrowthInr != null ? yoyData.prevYoyGrowthInr + '%' : '—' }}</div>
          </div>
        </div>

        <!-- 12-Month Side-by-Side Seasonality Comparison -->
        <div class="card yoy-chart-card" style="margin-top:16px;" v-if="yoyData && yoyData.comparison.length">
          <div class="chart-head">
            <div>
              <div class="chart-title"><v-icon size="18" color="#14663a">mdi-chart-bar</v-icon> 12-Month Inflow Trajectory Comparison (April – March)</div>
              <div class="chart-sub">Comparing monthly run-rates and donation seasonality across financial years</div>
            </div>
            <div class="chart-legends">
              <span class="cleg cur"><span class="cleg-box c1"></span> FY {{ yoyData.comparison[0].financialYear }} (Current)</span>
              <span class="cleg p1"><span class="cleg-box c2"></span> FY {{ yoyData.comparison[1].financialYear }}</span>
              <span class="cleg p2"><span class="cleg-box c3"></span> FY {{ yoyData.comparison[2].financialYear }}</span>
            </div>
          </div>

          <div class="chart-bars-wrap">
            <div class="month-col" v-for="(m, idx) in yoyData.monthLabels" :key="m">
              <div class="bars-group">
                <div class="bar bar-c1" :style="{ height: getBarHeight(yoyData.comparison[0].months[idx].inr) + '%' }" :title="'FY ' + yoyData.comparison[0].financialYear + ' ' + m + ': ' + formatMoney(yoyData.comparison[0].months[idx].inr)"></div>
                <div class="bar bar-c2" :style="{ height: getBarHeight(yoyData.comparison[1].months[idx].inr) + '%' }" :title="'FY ' + yoyData.comparison[1].financialYear + ' ' + m + ': ' + formatMoney(yoyData.comparison[1].months[idx].inr)"></div>
                <div class="bar bar-c3" :style="{ height: getBarHeight(yoyData.comparison[2].months[idx].inr) + '%' }" :title="'FY ' + yoyData.comparison[2].financialYear + ' ' + m + ': ' + formatMoney(yoyData.comparison[2].months[idx].inr)"></div>
              </div>
              <div class="month-label">{{ m }}</div>
            </div>
          </div>
        </div>

        <!-- Top Donation Categories / Appeals Across Years -->
        <div class="grid grid-2" style="margin-top:16px;" v-if="yoyData && yoyData.comparison.length">
          <div class="panel">
            <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-tag-multiple-outline</v-icon> Current FY Top Appeals</div>
            <ul class="cat-list">
              <li v-for="c in yoyData.comparison[0].categoriesList.slice(0, 6)" :key="c.name">
                <span class="cat-name">{{ c.name }}</span>
                <span class="cat-amt">{{ formatMoney(c.amount) }}</span>
              </li>
              <li v-if="!yoyData.comparison[0].categoriesList.length" class="dim">No categorized donations recorded yet.</li>
            </ul>
          </div>
          <div class="panel">
            <div class="panel-title mb"><v-icon size="18" color="#14663a">mdi-history</v-icon> Previous FY Top Appeals</div>
            <ul class="cat-list">
              <li v-for="c in yoyData.comparison[1].categoriesList.slice(0, 6)" :key="c.name">
                <span class="cat-name">{{ c.name }}</span>
                <span class="cat-amt">{{ formatMoney(c.amount) }}</span>
              </li>
              <li v-if="!yoyData.comparison[1].categoriesList.length" class="dim">No categorized donations recorded.</li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";

function empty() {
  return {
    adoption: { assigned: 0, submitted: 0, approved: 0, notStarted: 0, submissionRate: 0, approvalRate: 0, employees: 0 },
    workingDaysElapsed: 0, employees: [], divisions: [], managers: [], probationWatch: [],
  };
}

export default {
  name: "CeoAnalytics",
  data() {
    return {
      activeTab: "scorecards", // 'scorecards' | 'talent_matrix' | 'projects' | 'donations_yoy'
      loading: true,
      error: "",
      financialYear: "",
      pmrAvailable: false,
      a: empty(),
      view: "top",
      search: "",
      divisionFilter: "",

      // 9-Box Talent Matrix
      talentData: null,
      talentSearch: "",
      talentDivFilter: "",
      selectedQuadrantKey: "",

      // Project Benchmarking
      projectList: [],
      projectSearch: "",
      projectDivFilter: "",
      projectFilterStatus: "all",

      // Multi-Year YoY Donations
      yoyData: null,

      wtiLogo: require("@/assets/images/wtilogo.png"),
    };
  },
  computed: {
    divisionNames() {
      return Array.from(new Set((this.a.employees || []).map((e) => e.division))).filter(Boolean).sort();
    },
    filteredEmployees() {
      const q = this.search.trim().toLowerCase();
      return this.a.employees.filter((e) => {
        if (this.divisionFilter && e.division !== this.divisionFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    shownEmployees() {
      const list = this.filteredEmployees;
      if (this.view === "top") return list.slice(0, 10);
      if (this.view === "attention") return list.slice().reverse().slice(0, 10);
      return list;
    },
    selectedQuadrant() {
      if (!this.talentData || !this.talentData.quadrants || !this.selectedQuadrantKey) return null;
      return this.talentData.quadrants[this.selectedQuadrantKey] || null;
    },
    selectedQuadrantEmps() {
      if (!this.selectedQuadrantKey) return [];
      return this.filteredQuadrantEmps(this.selectedQuadrantKey);
    },
    projectDivisions() {
      return Array.from(new Set((this.projectList || []).map((p) => p.division))).filter(Boolean).sort();
    },
    filteredProjects() {
      const q = this.projectSearch.trim().toLowerCase();
      return (this.projectList || []).filter((p) => {
        if (this.projectDivFilter && p.division !== this.projectDivFilter) return false;
        if (this.projectFilterStatus !== "all" && p.status !== this.projectFilterStatus) return false;
        if (q && !((p.projectName || "").toLowerCase().includes(q) || (p.location || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    avgProjectTeamSize() {
      if (!this.projectList.length) return 0;
      const sum = this.projectList.reduce((acc, p) => acc + (p.teamSize || 0), 0);
      return Math.round((sum / this.projectList.length) * 10) / 10;
    },
    maxYoyMonthlyInr() {
      if (!this.yoyData || !this.yoyData.comparison) return 1;
      let max = 1;
      this.yoyData.comparison.forEach((fy) => {
        (fy.months || []).forEach((m) => {
          if (m.inr > max) max = m.inr;
        });
      });
      return max;
    },
  },
  mounted() {
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }
    this.fetchAllData();
  },
  methods: {
    authHeaders() { return { "x-auth-token": localStorage.getItem("token") }; },
    switchTab(tab) {
      this.activeTab = tab;
      if (this.$route.query.tab !== tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
      }
    },
    async fetchAllData() {
      this.loading = true;
      this.error = "";
      try {
        const [resAnalytics, resTalent, resProjects, resYoy] = await Promise.all([
          axios.get("dashboard/ceo/analytics", { headers: this.authHeaders() }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/talent-matrix", { headers: this.authHeaders() }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/projects-benchmark", { headers: this.authHeaders() }).catch(() => ({ data: null })),
          axios.get("dashboard/ceo/donations-yoy", { headers: this.authHeaders() }).catch(() => ({ data: null })),
        ]);

        if (resAnalytics.data && resAnalytics.data.status) {
          this.a = Object.assign(empty(), resAnalytics.data.data);
          this.financialYear = resAnalytics.data.financialYear || "";
          this.pmrAvailable = !!resAnalytics.data.pmrAvailable;
        }

        if (resTalent.data && resTalent.data.status) {
          this.talentData = resTalent.data.data;
        }

        if (resProjects.data && resProjects.data.status) {
          this.projectList = resProjects.data.data || [];
        }

        if (resYoy.data && resYoy.data.status) {
          this.yoyData = resYoy.data.data;
        }
      } catch (e) {
        this.error = "Unable to reach the server.";
      } finally {
        this.loading = false;
      }
    },
    filteredQuadrantEmps(quadrantKey) {
      if (!this.talentData || !this.talentData.quadrants || !this.talentData.quadrants[quadrantKey]) return [];
      const q = this.talentSearch.trim().toLowerCase();
      return (this.talentData.quadrants[quadrantKey].emps || []).filter((e) => {
        if (this.talentDivFilter && e.division !== this.talentDivFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        return true;
      });
    },
    getQuadrantCount(keys) {
      if (!this.talentData || !this.talentData.quadrants) return 0;
      return keys.reduce((acc, k) => acc + (this.filteredQuadrantEmps(k).length || 0), 0);
    },
    selectQuadrant(key) {
      this.selectedQuadrantKey = this.selectedQuadrantKey === key ? "" : key;
    },
    getBarHeight(amt) {
      if (!amt || this.maxYoyMonthlyInr <= 0) return 4;
      return Math.max(6, Math.min(100, Math.round((amt / this.maxYoyMonthlyInr) * 100)));
    },
    scoreColor(s) { return s >= 70 ? "#2e7d32" : s >= 40 ? "#e08600" : "#c62828"; },
    goBack() { this.$router.push("/ceo-dashboard"); },
    logout() { this.$store.commit("logoutUser"); },
    num(n) { return Number(n || 0).toLocaleString("en-IN"); },
    formatMoney(n) { return "₹" + Number(n || 0).toLocaleString("en-IN"); },
    formatUsd(n) { return "$" + Number(n || 0).toLocaleString("en-US"); },
  },
};
</script>

<style scoped>
.an-root { min-height: 100vh; width: 100%; background: linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%); font-family: "Poppins", "Segoe UI", system-ui, sans-serif; color: #1b2320; }
.an-topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; padding: 12px 26px; background: rgba(255,255,255,0.96); backdrop-filter: saturate(180%) blur(12px); border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 12px; }
.back-btn { background: #fff; border: 1px solid rgba(20,102,58,0.2); border-radius: 10px; width: 38px; height: 38px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.back-btn:hover { background: #eef6f0; }
.brand-logo-box { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #14663a, #2e7d32); box-shadow: 0 6px 16px rgba(20,102,58,0.3); padding: 6px; }
.brand-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
.brand-title { font-size: 17px; font-weight: 700; }
.brand-sub { font-size: 11.5px; color: #6b7a72; margin-top: -2px; }
.topbar-actions { display: flex; align-items: center; gap: 12px; }
.fy-chip { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; border: 1px solid #cfe5d8; padding: 5px 12px; border-radius: 20px; }
.btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 10px; font-family: inherit; }
.btn.small { padding: 6px 10px; font-size: 12px; }
.btn.ghost { background: #fff; color: #14663a; border: 1px solid rgba(20,102,58,0.2); }
.btn.solid { background: linear-gradient(135deg, #14663a, #2e7d32); color: #fff; }
.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }
.loader-wrap { display: flex; justify-content: center; align-items: center; min-height: 70vh; }

/* Navigation Tab Bar */
.an-nav { background: #fff; border-bottom: 1px solid #e1ebe4; padding: 0 26px; }
.nav-tabs { max-width: 1280px; margin: 0 auto; display: flex; gap: 8px; overflow-x: auto; }
.nav-tab { display: inline-flex; align-items: center; gap: 8px; background: transparent; border: none; border-bottom: 3px solid transparent; padding: 14px 16px; font-family: inherit; font-size: 13.5px; font-weight: 600; color: #6b7a72; cursor: pointer; white-space: nowrap; transition: all 0.2s; }
.nav-tab:hover { color: #14663a; background: #f7faf8; }
.nav-tab.active { color: #14663a; border-bottom-color: #14663a; }
.nav-tab .badge { background: #e8f3ec; color: #14663a; font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 10px; }

.an-body { max-width: 1280px; margin: 0 auto; padding: 22px 26px 44px; }
.banner.error { display: flex; gap: 8px; background: #fdecec; color: #c62828; border: 1px solid #f5c6c6; padding: 10px 14px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; }
.section-eyebrow { display: flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #14663a; margin: 18px 2px 12px; }
.eye-sub { text-transform: none; letter-spacing: 0; font-weight: 500; color: #8a978f; font-size: 11.5px; margin-left: 6px; }
.insight { display: flex; gap: 8px; align-items: flex-start; background: #fdf6ea; border: 1px solid #f2ddb8; color: #7a5a1e; padding: 11px 14px; border-radius: 12px; font-size: 13px; margin-bottom: 14px; }
.insight b { color: #5a4110; }

.funnel { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; background: #fff; border: 1px solid #edf2ee; border-radius: 16px; padding: 18px 20px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); }
.fn-step { flex: 1; min-width: 120px; text-align: center; }
.fn-step.warn { background: #fdf3e3; border-radius: 12px; padding: 6px; }
.fn-v { font-size: 26px; font-weight: 700; color: #14432a; }
.fn-l { font-size: 12px; color: #6b7a72; margin-top: 2px; } .fn-l em { font-style: normal; color: #14663a; font-weight: 700; }

.card { background: #fff; border-radius: 16px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; margin-bottom: 16px; }
.table-card { overflow-x: auto; }
.an-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.an-table thead th { text-align: left; background: #f7faf8; color: #8a978f; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; padding: 10px 14px; white-space: nowrap; }
.an-table td { padding: 10px 14px; border-top: 1px solid #f1f4f2; color: #33403a; }
.an-table .r { text-align: right; }
.strong { font-weight: 600; color: #23302a; }
.dim { color: #9aa8a1; }
.bad { color: #c62828; font-weight: 700; }
.warn-txt { color: #b56b00; font-weight: 700; }
.center { text-align: center; }
.proj-chip { font-size: 11px; background: #eef6f0; color: #14663a; padding: 3px 9px; border-radius: 12px; white-space: nowrap; }
.fr-tag { font-size: 10px; font-weight: 700; color: #1565c0; background: #e3f2fd; padding: 2px 6px; border-radius: 8px; margin-left: 6px; }
.prob-tag { font-size: 10px; font-weight: 700; color: #b56b00; background: #fbf1e2; padding: 1px 7px; border-radius: 10px; margin-left: 6px; }
.score-cell { display: flex; align-items: center; gap: 8px; min-width: 120px; }
.score-bar { flex: 1; height: 8px; background: #eef2f0; border-radius: 5px; overflow: hidden; }
.score-fill { height: 100%; border-radius: 5px; }
.score-num { font-weight: 700; font-size: 12.5px; width: 34px; text-align: right; }

.controls { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 12px; }
.view-toggle { display: flex; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; overflow: hidden; }
.vt { border: none; background: transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #5a6b62; padding: 9px 16px; }
.vt.on { background: #14663a; color: #fff; }
.filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.search-box { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 7px 12px; }
.search-box input { border: none; outline: none; font-family: inherit; font-size: 13px; background: transparent; width: 180px; color: #23302a; }
.sel { font-family: inherit; font-size: 13px; color: #23302a; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 8px 12px; cursor: pointer; }

/* 9-Box Matrix Styles */
.matrix-overview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 14px; }
.mo-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; box-shadow: 0 2px 10px rgba(0,0,0,0.03); }
.mo-card.star { border-left: 4px solid #2e7d32; }
.mo-card.core { border-left: 4px solid #1565c0; }
.mo-card.risk { border-left: 4px solid #c62828; }
.mo-val { font-size: 24px; font-weight: 700; color: #14432a; }
.mo-title { font-size: 13px; font-weight: 700; color: #23302a; margin-top: 2px; }
.mo-sub { font-size: 11px; color: #7a8880; }

.matrix-legend { display: flex; align-items: center; gap: 14px; font-size: 12px; color: #5a6b62; }
.leg-item { display: flex; align-items: center; gap: 5px; font-weight: 600; }
.leg-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.leg-dot.green { background: #2e7d32; }
.leg-dot.blue { background: #1565c0; }
.leg-dot.red { background: #c62828; }

.matrix-container { padding: 20px; display: flex; gap: 12px; align-items: stretch; }
.matrix-y-axis-label { writing-mode: vertical-rl; transform: rotate(180deg); font-size: 12px; font-weight: 700; color: #6b7a72; text-align: center; letter-spacing: 0.5px; }
.matrix-wrapper { flex: 1; }
.matrix-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.matrix-cell { background: #fbfdfc; border: 1px solid #dce8e0; border-radius: 12px; padding: 14px; min-height: 105px; cursor: pointer; transition: all 0.2s; }
.matrix-cell:hover { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.08); border-color: #14663a; }
.matrix-cell.selected { border: 2px solid #14663a; background: #eef6f0; }
.mc-head { display: flex; align-items: center; justify-content: space-between; }
.mc-tag { font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 8px; }
.mc-tag.green { background: #e8f5e9; color: #2e7d32; }
.mc-tag.blue { background: #e3f2fd; color: #1565c0; }
.mc-tag.orange { background: #fff3e0; color: #e65100; }
.mc-tag.red { background: #ffebee; color: #c62828; }
.mc-count { font-size: 18px; font-weight: 800; color: #14432a; }
.mc-desc { font-size: 11px; color: #7a8880; margin-top: 8px; line-height: 1.3; }
.matrix-x-axis-label { font-size: 12px; font-weight: 700; color: #6b7a72; text-align: center; margin-top: 10px; letter-spacing: 0.5px; }

.quadrant-detail-box { padding: 18px 20px; border-left: 4px solid #14663a; }
.qd-head { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
.qd-title { font-size: 16px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 7px; }
.qd-sub { font-size: 12px; color: #6b7a72; margin-top: 2px; }

/* Project Benchmarking */
.proj-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
.pk-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.pk-card.top { border-left: 4px solid #2e7d32; }
.pk-card.warn { border-left: 4px solid #c62828; }
.pk-val { font-size: 24px; font-weight: 700; color: #14432a; }
.pk-lbl { font-size: 12px; color: #6b7a72; margin-top: 2px; }
.status-pill { font-size: 10.5px; font-weight: 700; padding: 3px 8px; border-radius: 10px; }
.status-pill.top { background: #e8f5e9; color: #2e7d32; }
.status-pill.lagging { background: #ffebee; color: #c62828; }
.status-pill.moderate { background: #e3f2fd; color: #1565c0; }

/* Multi-Year YoY Donations */
.yoy-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 14px; }
.yoy-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.yoy-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.yoy-fy { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; padding: 2px 8px; border-radius: 10px; }
.yoy-tx { font-size: 11px; color: #7a8880; }
.yoy-val { font-size: 22px; font-weight: 800; color: #14432a; }
.yoy-sub { font-size: 11.5px; color: #6b7a72; margin-top: 2px; }
.yoy-foot { font-size: 11px; color: #8a978f; margin-top: 4px; border-top: 1px dashed #e5ede7; padding-top: 4px; }

.yoy-growth-card { background: #fff; border-radius: 14px; padding: 16px; border: 1px solid #edf2ee; }
.yoy-growth-card.pos { border-left: 4px solid #2e7d32; }
.yoy-growth-card.neg { border-left: 4px solid #c62828; }
.yg-lbl { font-size: 11.5px; font-weight: 600; color: #6b7a72; }
.yg-val { font-size: 24px; font-weight: 800; display: flex; align-items: center; gap: 4px; margin-top: 4px; color: #14432a; }
.yg-sub { font-size: 11px; color: #8a978f; margin-top: 2px; }

.yoy-chart-card { padding: 20px; }
.chart-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 12px; }
.chart-title { font-size: 15px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 6px; }
.chart-sub { font-size: 11.5px; color: #6b7a72; }
.chart-legends { display: flex; align-items: center; gap: 14px; font-size: 11.5px; font-weight: 600; }
.cleg { display: flex; align-items: center; gap: 5px; color: #43544c; }
.cleg-box { width: 12px; height: 12px; border-radius: 3px; }
.cleg-box.c1 { background: #14663a; }
.cleg-box.c2 { background: #43a047; }
.cleg-box.c3 { background: #a5d6a7; }

.chart-bars-wrap { display: flex; justify-content: space-between; align-items: flex-end; height: 180px; padding: 10px 0; border-bottom: 2px solid #e1ebe4; }
.month-col { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; justify-content: flex-end; }
.bars-group { display: flex; align-items: flex-end; gap: 3px; height: 140px; }
.bar { width: 10px; border-radius: 3px 3px 0 0; transition: height 0.4s; }
.bar.bar-c1 { background: #14663a; }
.bar.bar-c2 { background: #43a047; }
.bar.bar-c3 { background: #a5d6a7; }
.month-label { font-size: 11px; font-weight: 600; color: #6b7a72; margin-top: 8px; }

.grid { display: grid; gap: 16px; }
.grid-2 { grid-template-columns: repeat(2, 1fr); }
.panel { background: #fff; border-radius: 14px; padding: 18px; border: 1px solid #edf2ee; box-shadow: 0 2px 10px rgba(0,0,0,0.03); }
.panel-title { font-size: 14px; font-weight: 700; color: #14432a; display: flex; align-items: center; gap: 6px; }
.cat-list { list-style: none; padding: 0; margin: 12px 0 0; }
.cat-list li { display: flex; justify-content: space-between; font-size: 12.5px; padding: 7px 0; border-bottom: 1px solid #f1f5f2; }
.cat-name { color: #33403a; font-weight: 500; }
.cat-amt { color: #14663a; font-weight: 700; }

@media (max-width: 900px) {
  .an-body { padding: 16px; }
  .funnel, .matrix-overview-grid, .proj-kpi-grid, .yoy-summary-grid, .grid-2 { grid-template-columns: 1fr; }
  .controls { flex-direction: column; align-items: stretch; }
  .matrix-container { flex-direction: column; }
  .matrix-y-axis-label { writing-mode: horizontal-tb; transform: none; margin-bottom: 6px; }
}
</style>
